import React from 'react';
import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/mode-handlebars';
import 'ace-builds/src-noconflict/theme-textmate';
import langTools from 'ace-builds/src-noconflict/ext-language_tools';

import * as Styled from './styled';
import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import { Controller, Control, FieldError } from 'react-hook-form';
import FormFieldWrapper from '@/components/Input/FormFieldWrapper';

export const PromptDisplay: React.FC<{ prompt: string }> = ({ prompt }) => {
  return <Styled.PromptDisplay className="prompt-display">{prompt}</Styled.PromptDisplay>;
};

export const PromptEditorControl: React.FC<
  Omit<PromptEditorProps, 'onChange' | 'prompt'> & {
    name: string;
    control?: Control<any, any>;
    error?: FieldError;
    label?: string;
    hint?: string;
  }
> = ({ control, name, error, label, hint, readOnly, ...rest }) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, height: '100%', width: '100%' }}>
          <FormFieldWrapper
            error={error}
            label={label}
            // labelSupplement={labelSupplement}
            hint={hint}
            // success={success}
            wide
          >
            <PromptEditor onChange={onChange} readOnly={readOnly} {...rest} prompt={value} />
          </FormFieldWrapper>
        </div>
      )}
    />
  );
};

type PromptEditorProps = React.HTMLProps<HTMLDivElement> & {
  prompt?: string;
  schema?: IDataFrameSchema;
  placeholder?: string;
  onChange: (prompt: string) => void;
  readOnly?: boolean;
  height?: number;
  minLines?: number;
  maxLines?: number;
};

const PromptEditor: React.FC<
  React.HTMLProps<HTMLDivElement> & {
    prompt?: string;
    schema?: IDataFrameSchema;
    placeholder?: string;
    error?: FieldError;
    onChange: (prompt: string) => void;
    readOnly?: boolean;
    height?: number;
    minLines?: number;
    maxLines?: number;
  }
> = ({
  prompt,
  schema,
  onChange,
  readOnly,
  error,
  height = 160,
  minLines = 6,
  maxLines = 12,
  placeholder = 'Enter your prompt here',
  ...rest
}) => {
  const schemaFieldsCompleter = {
    getCompletions: function (editor: any, session: any, pos: any, prefix: any, callback: any) {
      const completions: any[] = [];
      schema?.fields.forEach(function (field) {
        completions.push({
          value: field.name,
          meta: field.dtype,
        });
      });
      callback(null, completions);
    },
  };

  langTools.addCompleter(schemaFieldsCompleter);

  return (
    <Styled.Editor $hasError={!!error} {...rest} $readOnly={readOnly}>
      <AceEditor
        mode="handlebars"
        theme="textmate"
        onChange={onChange}
        name="queryPromptEditor"
        height={`${height}px`}
        value={prompt}
        width="100%"
        wrapEnabled={true}
        showPrintMargin={false}
        className="prompt-input"
        minLines={minLines}
        maxLines={maxLines}
        placeholder={placeholder}
        fontSize={14}
        lineHeight={24}
        editorProps={{ $blockScrolling: true }}
        onLoad={function (editor) {
          editor.renderer.setPadding(10);
          editor.renderer.setScrollMargin(10, 10, 0, 0);
          editor.completers = [schemaFieldsCompleter];
          editor.focus();
          editor.navigateFileEnd();
        }}
        setOptions={{
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          // enableSnippets: true,
          showGutter: false,
          highlightActiveLine: false,
          showLineNumbers: false,
          readOnly: !!readOnly,
          tabSize: 2,
        }}
      />
    </Styled.Editor>
  );
};

export default PromptEditor;
